import { OutputNode } from "tone";

export namespace RydMixer {
    export type TrackParams = {
        id: number;
        name: string;
        gain: number;
        pan: number;
        muted: boolean;
        solo: boolean;
        onBus: boolean;
    };

    export type TrackInitParams = TrackParams & {
        sourceNode?: OutputNode; // This could be an oscillator, buffer, or instrument output
    }

    export class TrackAudio {
        gainNode: GainNode;
        panNode: StereoPannerNode;
        constructor(
            public audioContext: AudioContext,
            public masterGain: GainNode,
            public sourceNode?: OutputNode,
        ) {
            this.gainNode = audioContext.createGain();
            this.panNode = audioContext.createStereoPanner();
            this.gainNode.connect(this.panNode);
            this.panNode.connect(masterGain);
            // Connect chain: source -> gain -> pan
            sourceNode?.connect(this.gainNode);
        }
        update({ gain, pan, muted }: { gain: number; pan: number; muted: boolean }) {
            this.gainNode.gain.value = muted ? 0 : gain;
            this.panNode.pan.value = pan;
        }
        disconnect() {
            this.sourceNode?.disconnect();
            this.gainNode.disconnect();
            this.panNode.disconnect();
        }
    }

    export class Mixer {
        tracks: Map<number, {
            audio: TrackAudio,
            params: TrackParams
        }> = new Map();

        masterGain: GainNode;
        activeTracks: Set<number> = new Set();
        soloTracks: Set<number> = new Set();

        constructor(public audioContext: AudioContext) {
            this.masterGain = audioContext.createGain();
            this.masterGain.connect(audioContext.destination);
        }

        addTrack(params: TrackInitParams) {
            const trackAudio = new TrackAudio(
                this.audioContext,
                this.masterGain,
                params.sourceNode,
            );
            trackAudio.update(params);
            // Connect chain: source -> gain -> pan -> master
            trackAudio.panNode?.connect(this.masterGain);
            this.tracks.set(params.id, { audio: trackAudio, params: params });
        }

        updateTrack(
            id: number,
            params: {
                gain: number;
                pan: number;
                muted: boolean,
                solo: boolean,
                onBus: boolean
            }) {

            const track = this.tracks.get(id);
            if (!track) return;

            const recomputeIds: Set<number> = params.solo === track.params.solo
                // no change in solo state, recompute only this track
                ? new Set([id])
                // change in solo state, recompute all tracks
                : new Set(this.tracks.keys());

            if (params.solo) {
                this.soloTracks.add(id);
            } else {
                this.soloTracks.delete(id);
            }

            if (params.onBus) {
                this.activeTracks.add(id);
            } else {
                this.activeTracks.delete(id);
            }

            track.params = { ...track.params, ...params };
            recomputeIds.forEach(tid => {
                const recomputeTrack = this.tracks.get(tid)!;

                const willMute = isTrackStateMuted(
                    recomputeTrack.params.id,
                    this.activeTracks,
                    this.soloTracks,
                    recomputeTrack.params.muted
                );
                recomputeTrack.audio.update({
                    ...params,
                    muted: willMute
                });
            });
        }

        removeTrack(id: number) {
            const track = this.tracks.get(id);
            if (track) {
                track.audio.disconnect();
                this.tracks.delete(id);
            }
            this.activeTracks.delete(id);
            this.soloTracks.delete(id);
        }

        removeAllTracks() {
            console.log('Removing all tracks from mixer');
            this.tracks.forEach(track => {
                this.removeTrack(track.params.id);
            });
            this.tracks.clear();
        }

        setMasterGain(value: number) {
            this.masterGain.gain.value = value;
        }

    }
    export function isTrackStateMuted(
        id: number,
        activeTracks: Set<number>,
        soloTracks: Set<number>,
        muted: boolean
    ): boolean {

        return !activeTracks.has(id)
            // track not on bus
            ? true
            : soloTracks.size > 0
                // there is some soloed tracks and this is not one of them
                ? !soloTracks.has(id)
                : muted; // no soloed tracks, use track mute state
    }
}